/**
 * @fileoverview Responsive density tokens for the unified inspection renderer.
 * @module inspection-renderer/tokens
 *
 * Responsibilities:
 * - Encode the plan D2 baseline: mobile look at all widths; ≥768 px relaxes
 *   the grid + shrinks touch targets from 44 px to 36 px.
 * - Provide a single `getDensity(opts)` function consumed by every widget
 *   wrapper so the visual baseline is consistent.
 *
 * Dependencies:
 * - None (no React, no DOM measurement).
 *
 * Architecture:
 * - Pure functions. Caller is expected to supply the viewport width OR pass
 *   `density: 'mobile' | 'desktop' | 'auto'` from `<InspectionRenderer>`
 *   props. `'auto'` uses the viewport width breakpoint.
 *
 * Notes:
 * - Tailwind class names returned here are utility-first and self-contained;
 *   no consuming-app `tailwind.config` change is required.
 *
 * @contract DensityTokens
 *
 * Purpose: per-density visual tokens consumed by every widget wrapper.
 *
 * Fields:
 * - mode: which density tier is active.
 * - touchTarget: minimum px size for interactive controls (44 mobile, 36 desktop).
 * - sectionColumnsMax: how many section columns may render (1 below 500 px,
 *   max 2 below 900 px, full at >=900 px).
 * - inputPadding: Tailwind padding utility class.
 * - rowGap: Tailwind row gap utility class.
 * - labelTextSize: Tailwind text-size utility class for labels.
 *
 * Rules:
 * - Deterministic. Same `mode` + viewport always yields the same tokens.
 */

export type DensityMode = 'mobile' | 'desktop';

export interface DensityTokens {
  mode: DensityMode;
  touchTarget: number;        // px
  sectionColumnsMax: number;  // 1 | 2 | 3
  inputPadding: string;       // tailwind utility
  rowGap: string;             // tailwind utility
  labelTextSize: string;      // tailwind utility
}

export interface GetDensityOpts {
  /** Caller's explicit density preference. */
  density?: 'auto' | 'mobile' | 'desktop';
  /** Effective viewport width in px (used when density === 'auto'). */
  viewportWidth?: number;
}

const MOBILE: DensityTokens = {
  mode: 'mobile',
  touchTarget: 44,
  sectionColumnsMax: 1,
  inputPadding: 'px-3 py-2',
  rowGap: 'gap-3',
  labelTextSize: 'text-sm',
};

const DESKTOP_TIGHT: DensityTokens = {
  mode: 'desktop',
  touchTarget: 36,
  sectionColumnsMax: 2,
  inputPadding: 'px-2.5 py-1.5',
  rowGap: 'gap-2',
  labelTextSize: 'text-sm',
};

const DESKTOP_WIDE: DensityTokens = {
  mode: 'desktop',
  touchTarget: 36,
  sectionColumnsMax: 3,
  inputPadding: 'px-2.5 py-1.5',
  rowGap: 'gap-2',
  labelTextSize: 'text-sm',
};

/**
 * @process getDensity
 *
 * Steps:
 * 1. If `density === 'mobile'`, return `MOBILE` tokens.
 * 2. If `density === 'desktop'`, return `DESKTOP_TIGHT` (single 2-col cap).
 * 3. If `density === 'auto'` (default), inspect `viewportWidth`:
 *    - < 768 px → MOBILE
 *    - 768–1023 px → DESKTOP_TIGHT (2 columns)
 *    - >= 1024 px → DESKTOP_WIDE (3 columns)
 *
 * Inputs: GetDensityOpts.
 * Outputs: DensityTokens.
 * Idempotency: REQUIRED.
 */
export function getDensity(opts: GetDensityOpts = {}): DensityTokens {
  const mode = opts.density ?? 'auto';
  if (mode === 'mobile') return MOBILE;
  if (mode === 'desktop') return DESKTOP_TIGHT;
  // 'auto'
  const w = opts.viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1024);
  if (w < 768) return MOBILE;
  if (w < 1024) return DESKTOP_TIGHT;
  return DESKTOP_WIDE;
}

/**
 * Clamp a section's declared columns to the active density's max.
 * Sections may declare 1/2/3; mobile clamps to 1; tablet clamps to 2.
 */
export function clampSectionColumns(declared: number, density: DensityTokens): number {
  return Math.max(1, Math.min(declared, density.sectionColumnsMax));
}
